import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, Award, Users, Clock, CheckCircle, ChevronRight, Play, Lock, FileCheck, Gavel, Scale, BadgeCheck, User } from 'lucide-react';
import Badge from '@/components/Badge';
import { Button } from '@/components/ui/button';
import { getCourseInfo } from '@/services/api';
import { useAuth } from '@/context/AuthContext';

const LandingPage = () => {
  const [courseInfo, setCourseInfo] = useState(null);
  const { isAuthenticated, hasPaid } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCourseInfo = async () => {
      try {
        const response = await getCourseInfo();
        setCourseInfo(response.data);
      } catch (error) {
        console.error('Failed to fetch course info:', error);
      }
    };
    fetchCourseInfo();
  }, []);

  const handleGetStarted = () => {
    if (isAuthenticated && hasPaid) {
      navigate('/dashboard');
    } else if (isAuthenticated) {
      navigate('/enroll');
    } else {
      navigate('/register');
    }
  };

  return (
    <div className="min-h-screen bg-background noise-bg">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Badge className="w-8 h-8" />
            <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
              COLORADO BAIL ENFORCEMENT
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => document.getElementById('compliance')?.scrollIntoView({ behavior: 'smooth' })}
              className="hidden md:inline-flex"
            >
              POST Compliance
            </Button>
            {isAuthenticated ? (
              <Button 
                onClick={() => navigate(hasPaid ? '/dashboard' : '/enroll')} 
                className="btn-primary"
                data-testid="nav-dashboard-btn"
              >
                {hasPaid ? 'DASHBOARD' : 'ENROLL NOW'}
              </Button>
            ) : (
              <>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/login')}
                  data-testid="nav-login-btn"
                >
                  Login
                </Button>
                <Button 
                  onClick={() => navigate('/register')} 
                  className="btn-primary"
                  data-testid="nav-register-btn"
                >
                  GET STARTED
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1740367217559-de1dcaafafe3?crop=entropy&cs=srgb&fm=jpg&q=85')`,
            filter: 'brightness(0.3)'
          }}
        />
        <div className="absolute inset-0 hero-gradient" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-6 animate-fadeIn">
              <span className="px-3 py-1 bg-primary/20 text-primary text-sm font-semibold rounded-sm uppercase tracking-wide">
                Colorado DOI Certification Pending
              </span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-black mb-6 animate-fadeIn stagger-1" style={{ fontFamily: 'Barlow Condensed' }}>
              COLORADO BAIL ENFORCEMENT
              <span className="block text-primary">CERTIFICATION COURSE</span>
            </h1>
            
            <p className="text-xl text-muted-foreground mb-8 animate-fadeIn stagger-2">
              The most comprehensive bail enforcement training in Colorado. 24 hours of instruction, 
              real case studies with body camera footage, and full DOI compliance tracking. 
              Built by a 28-year industry veteran.
            </p>
            
            <div className="flex flex-wrap gap-4 animate-fadeIn stagger-3">
              <Button 
                onClick={handleGetStarted} 
                className="btn-primary text-lg px-8 py-4 gold-glow"
                data-testid="hero-get-started-btn"
              >
                GET CERTIFIED <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                variant="outline" 
                className="btn-secondary text-lg px-8 py-4"
                onClick={() => document.getElementById('course-overview')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-learn-more-btn"
              >
                LEARN MORE
              </Button>
            </div>

            <div className="flex flex-wrap gap-8 mt-12 animate-fadeIn stagger-4">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                <span className="text-muted-foreground">24 Credit Hours</span>
              </div>
              <div className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-primary" />
                <span className="text-muted-foreground">10 Comprehensive Chapters</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-primary" />
                <span className="text-muted-foreground">Certificate of Completion</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-card border-y border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-black text-primary mb-2" style={{ fontFamily: 'Barlow Condensed' }}>24</div>
              <div className="text-muted-foreground">Credit Hours</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-black text-primary mb-2" style={{ fontFamily: 'Barlow Condensed' }}>10</div>
              <div className="text-muted-foreground">Chapters</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-black text-primary mb-2" style={{ fontFamily: 'Barlow Condensed' }}>150</div>
              <div className="text-muted-foreground">Exam Questions</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-black text-primary mb-2" style={{ fontFamily: 'Barlow Condensed' }}>28</div>
              <div className="text-muted-foreground">Years Experience</div>
            </div>
          </div>
        </div>
      </section>

      {/* Eligibility Requirements Section */}
      <section id="requirements" className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              ELIGIBILITY <span className="text-primary">REQUIREMENTS</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Before enrolling, ensure you meet these Colorado requirements for bail enforcement certification.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-card border-2 border-primary/20 rounded-sm p-8">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center shrink-0">
                    <User className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Age Requirement</h3>
                    <p className="text-muted-foreground">Must be at least <strong className="text-foreground">18 years old</strong></p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center shrink-0">
                    <BadgeCheck className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Citizenship</h3>
                    <p className="text-muted-foreground">Must be a <strong className="text-foreground">U.S. citizen</strong> or resident alien</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center shrink-0">
                    <Scale className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Residency</h3>
                    <p className="text-muted-foreground">Must be a <strong className="text-foreground">resident of Colorado</strong></p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center shrink-0">
                    <FileCheck className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Criminal Record</h3>
                    <p className="text-muted-foreground">No felony convictions within the <strong className="text-foreground">last 10 years</strong> and at least 10 years clear from completion of any parole or probation</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-border text-center">
                <p className="text-sm text-muted-foreground">
                  These requirements apply to licensure or employment by a bondsman in Colorado.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Course Overview */}
      <section id="course-overview" className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              COURSE <span className="text-primary">CURRICULUM</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Our comprehensive curriculum exceeds Colorado POST requirements by 6x, covering everything 
              from constitutional law to practical field operations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {courseInfo?.chapters?.map((chapter, index) => (
              <div 
                key={index}
                className="p-6 bg-card border border-border rounded-sm card-hover flex items-start gap-4"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center shrink-0">
                  <span className="text-primary font-bold text-lg" style={{ fontFamily: 'Barlow Condensed' }}>
                    {chapter.number}
                  </span>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">{chapter.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-card border-y border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              WHY THIS <span className="text-primary">COURSE?</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Built to fill a void in Colorado bail enforcement training with current, comprehensive, 
              and actually taught content.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-8 bg-background border border-border rounded-sm">
              <Play className="w-10 h-10 text-primary mb-4" />
              <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Barlow Condensed' }}>REAL CASE STUDIES</h3>
              <p className="text-muted-foreground">
                Learn from actual cases including body camera footage and interrogation recordings. 
                See theory in action with State v. Ken Hill.
              </p>
            </div>

            <div className="p-8 bg-background border border-border rounded-sm">
              <FileCheck className="w-10 h-10 text-primary mb-4" />
              <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Barlow Condensed' }}>DOI COMPLIANT</h3>
              <p className="text-muted-foreground">
                Full audit trail tracking, time-on-task verification, and documentation 
                ready for Colorado Division of Insurance review.
              </p>
            </div>

            <div className="p-8 bg-background border border-border rounded-sm">
              <Gavel className="w-10 h-10 text-primary mb-4" />
              <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Barlow Condensed' }}>CURRENT LAW</h3>
              <p className="text-muted-foreground">
                Updated with HB 23-1293 and all current Colorado statutes. Not outdated DVDs 
                from years ago.
              </p>
            </div>

            <div className="p-8 bg-background border border-border rounded-sm">
              <Award className="w-10 h-10 text-primary mb-4" />
              <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Barlow Condensed' }}>CERTIFICATE</h3>
              <p className="text-muted-foreground">
                Verifiable Certificate of Completion with unique tracking number. 
                Admin-approved after essay review.
              </p>
            </div>

            <div className="p-8 bg-background border border-border rounded-sm">
              <Users className="w-10 h-10 text-primary mb-4" />
              <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Barlow Condensed' }}>RECOVERY BOARD</h3>
              <p className="text-muted-foreground">
                Graduates get exclusive access to our Recovery Board - connecting certified 
                agents with bondsmen seeking recovery services.
              </p>
            </div>

            <div className="p-8 bg-background border border-border rounded-sm">
              <Clock className="w-10 h-10 text-primary mb-4" />
              <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Barlow Condensed' }}>SELF-PACED</h3>
              <p className="text-muted-foreground">
                Complete the course at your own pace with progress tracking. 
                Audio narration available for every chapter.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* POST Compliance Section */}
      <section id="compliance" className="py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Scale className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-4xl font-black mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              POST COMPLIANCE <span className="text-primary">& COLORADO LAW</span>
            </h2>
            <p className="text-slate-300 max-w-3xl mx-auto">
              This course exceeds all Colorado statutory requirements for bail enforcement training. 
              Full compliance mapping available for DOI auditors and bondsmen.
            </p>
          </div>

          {/* Statutory Authority */}
          <div className="mb-12 p-6 bg-slate-800/50 rounded-lg border border-slate-700">
            <h3 className="text-xl font-bold mb-4 text-primary" style={{ fontFamily: 'Barlow Condensed' }}>
              STATUTORY AUTHORITY
            </h3>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div className="p-4 bg-slate-800 rounded">
                <div className="font-semibold text-white mb-1">C.R.S. § 12-7-105.5</div>
                <div className="text-slate-400">Bail Recovery Training Requirement</div>
              </div>
              <div className="p-4 bg-slate-800 rounded">
                <div className="font-semibold text-white mb-1">C.R.S. § 24-31-303(1)(h)</div>
                <div className="text-slate-400">POST Board Standards for Bail Recovery</div>
              </div>
              <div className="p-4 bg-slate-800 rounded">
                <div className="font-semibold text-white mb-1">C.R.S. § 10-2-301</div>
                <div className="text-slate-400">Continuing Education Requirement</div>
              </div>
            </div>
          </div>

          {/* POST E-212 Compliance Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="p-6 bg-slate-800/50 rounded-lg border border-slate-700">
              <div className="flex items-center gap-2 mb-4">
                <BadgeCheck className="w-6 h-6 text-green-500" />
                <h4 className="font-bold text-lg">Topic Area 1</h4>
              </div>
              <h5 className="text-primary font-semibold mb-2">Colorado Criminal Code</h5>
              <ul className="text-sm text-slate-300 space-y-1">
                <li>• Applicable offenses & classifications</li>
                <li>• Obstruction of justice statutes</li>
                <li>• Impersonation of law enforcement</li>
                <li>• Weapons offenses (C.R.S. § 18-12)</li>
                <li>• Kidnapping statutes</li>
              </ul>
              <div className="mt-4 px-3 py-1 bg-green-500/20 text-green-400 text-xs font-semibold rounded inline-block">
                COVERED - Chapters 1-9
              </div>
            </div>

            <div className="p-6 bg-slate-800/50 rounded-lg border border-slate-700">
              <div className="flex items-center gap-2 mb-4">
                <BadgeCheck className="w-6 h-6 text-green-500" />
                <h4 className="font-bold text-lg">Topic Area 2</h4>
              </div>
              <h5 className="text-primary font-semibold mb-2">Principles of Criminal Culpability</h5>
              <ul className="text-sm text-slate-300 space-y-1">
                <li>• C.R.S. § 18-1-501 (Mental States)</li>
                <li>• C.R.S. § 18-1-502 (Culpability)</li>
                <li>• C.R.S. § 18-1-503 (Elements)</li>
                <li>• Complicity & accessory liability</li>
                <li>• Justification defenses</li>
              </ul>
              <div className="mt-4 px-3 py-1 bg-green-500/20 text-green-400 text-xs font-semibold rounded inline-block">
                COVERED - Chapters 3, 5
              </div>
            </div>

            <div className="p-6 bg-slate-800/50 rounded-lg border border-slate-700">
              <div className="flex items-center gap-2 mb-4">
                <BadgeCheck className="w-6 h-6 text-green-500" />
                <h4 className="font-bold text-lg">Topic Area 3</h4>
              </div>
              <h5 className="text-primary font-semibold mb-2">Seizure & Entry Law</h5>
              <ul className="text-sm text-slate-300 space-y-1">
                <li>• Fourth Amendment application</li>
                <li>• Third-party residence entry</li>
                <li>• Consent search requirements</li>
                <li>• Castle Doctrine (C.R.S. § 18-1-704.5)</li>
                <li>• 42 U.S.C. § 1983 civil rights</li>
              </ul>
              <div className="mt-4 px-3 py-1 bg-green-500/20 text-green-400 text-xs font-semibold rounded inline-block">
                COVERED - Chapter 2
              </div>
            </div>
          </div>

          {/* Hours Comparison */}
          <div className="p-6 bg-gradient-to-r from-primary/20 to-primary/5 rounded-lg border border-primary/30">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h4 className="text-2xl font-black mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  EXCEEDS MINIMUM BY <span className="text-primary">1.5x</span>
                </h4>
                <p className="text-slate-300">
                  Colorado law requires 16 hours minimum (C.R.S. § 12-7-105.5). This course provides 24 hours 
                  of comprehensive instruction with all three POST E-212 topic areas covered.
                </p>
              </div>
              <div className="text-center shrink-0">
                <div className="text-5xl font-black text-primary" style={{ fontFamily: 'Barlow Condensed' }}>
                  24
                </div>
                <div className="text-sm text-slate-400">Credit Hours</div>
                <div className="text-xs text-slate-500 mt-1">(16 required)</div>
              </div>
            </div>
          </div>

          {/* Documentation Note */}
          <div className="mt-8 text-center text-sm text-slate-400">
            <p>
              Full POST E-212 Compliance Map available upon request for DOI auditors and bondsmen. 
              All certificates include chapter-by-chapter time tracking for audit verification.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-black mb-6" style={{ fontFamily: 'Barlow Condensed' }}>
            READY TO GET <span className="text-primary">CERTIFIED?</span>
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            Join the most comprehensive bail enforcement training program in Colorado. 
            Start your certification journey today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={handleGetStarted}
              className="btn-primary text-lg px-8 py-4 gold-glow"
              data-testid="cta-get-started-btn"
            >
              ENROLL NOW - ${courseInfo?.price || 495}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mt-4">
            100% discount codes available for qualifying agencies
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-card border-t border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Badge className="w-6 h-6" />
              <span className="font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
                COLORADO BAIL ENFORCEMENT CERTIFICATION
              </span>
            </div>
            <div className="text-sm text-muted-foreground">
              Built for Colorado. Exceeding DOI Standards.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
